import {
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue,
} from "@f2e/ui/components/base"
import { SelectItemWithDescription } from "@f2e/ui/components/base-plus"
import { useTranslation } from "react-i18next"

import type { LLMModel } from "@/hooks/api/llm"
import { useStore } from "@/store"

import { resolveLocalizedText } from "../registry"
import type {
  AgentTemplateAgentModelFieldDefinition,
  AgentTemplateAgentModelValue,
} from "../types"
import { ConfigurableFieldShell } from "./ConfigurableFieldShell"
import type { TemplateItemRendererProps } from "./TemplateItemRenderer"

export const getEligibleAgentModels = (
  definition: AgentTemplateAgentModelFieldDefinition,
  availableModels: LLMModel[]
): LLMModel[] => {
  const allowedModelIds = new Set(definition.allowedModelIds)
  const requiredModalities = new Set(definition.requiredInputModalities)
  return availableModels.filter((model) => {
    if (model.modelType !== "text") return false
    if (allowedModelIds.size > 0 && !allowedModelIds.has(model.modelId)) {
      return false
    }
    const modalities = new Set(
      model.inputModalities.map((modality) => modality.trim().toLowerCase())
    )
    return [...requiredModalities].every((modality) => modalities.has(modality))
  })
}

export const AgentModelItemRenderer = ({
  item,
  locale,
  value,
  error,
  disabled,
  onChange,
}: TemplateItemRendererProps) => {
  const { t } = useTranslation()
  const availableModels = useStore((state) => state.availableModels)
  if (item.type !== "agent_model") return null

  const definition = item as AgentTemplateAgentModelFieldDefinition
  const models = getEligibleAgentModels(definition, availableModels)
  const inputId = `template-${definition.key}`
  const configuredValue = value as AgentTemplateAgentModelValue | undefined
  const modelValue: AgentTemplateAgentModelValue = configuredValue ?? {
    enabled: definition.required || Boolean(definition.defaultModelId),
    ...(definition.defaultModelId
      ? { modelId: definition.defaultModelId }
      : {}),
  }
  const enabled = definition.required || modelValue.enabled
  const availabilityId =
    models.length === 0 ? `${inputId}-availability` : undefined

  return (
    <ConfigurableFieldShell
      id={inputId}
      title={resolveLocalizedText(definition.label, locale)}
      description={
        definition.description
          ? resolveLocalizedText(definition.description, locale)
          : undefined
      }
      enabled={enabled}
      required={definition.required}
      disabled={disabled}
      error={error}
      onEnabledChange={(enabled) => onChange({ ...modelValue, enabled })}
    >
      <Select
        value={modelValue.modelId || ""}
        disabled={disabled || models.length === 0}
        onValueChange={(modelId) =>
          onChange({
            enabled: true,
            modelId,
          } satisfies AgentTemplateAgentModelValue)
        }
      >
        <SelectTrigger
          id={`${inputId}-model`}
          aria-labelledby={`${inputId}-title`}
          aria-required={definition.required}
          aria-invalid={!!error}
          aria-describedby={[
            definition.description ? `${inputId}-description` : undefined,
            availabilityId,
            error ? `${inputId}-error` : undefined,
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <SelectValue
            placeholder={t("workspace.agent.templates.agentModel.placeholder")}
          />
        </SelectTrigger>
        <SelectContent>
          {models.map((model) => (
            <SelectItemWithDescription
              key={model.modelId}
              value={model.modelId}
              description={model.provider}
            >
              {model.displayName}
            </SelectItemWithDescription>
          ))}
        </SelectContent>
      </Select>
      {models.length === 0 && (
        <p id={availabilityId} className="text-sm text-destructive">
          {t("workspace.agent.templates.agentModel.noCompatibleModels")}
        </p>
      )}
    </ConfigurableFieldShell>
  )
}
